import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { whyChooseUs } from "@/mocks/home";

export default function WhyChooseSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.08 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why"
      ref={sectionRef}
      className="py-24 md:py-32 bg-white"
    >
      <div className="px-6 lg:px-12 xl:px-20">
        <div className="reveal text-center mb-16 md:mb-20">
          <span className="text-xs font-semibold tracking-[0.2em] text-secondary-600 uppercase mb-4 block">
            Our Advantage
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 tracking-tight">
            {t("why.title")}
          </h2>
          <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {t("why.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {whyChooseUs.map((item, index) => (
            <div
              key={item.id}
              className="reveal group flex gap-5 p-6 rounded-xl border border-foreground-100/50 hover:border-foreground-200 hover:bg-background-50 transition-all duration-300"
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="w-10 h-10 rounded-lg bg-foreground-950 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500 transition-colors duration-300">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-white text-sm`} />
                </div>
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground-950 mb-1.5 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}